/**
 * Zod schemas for authored content (CONTENT-MODEL.md).
 *
 * Content is data, so it gets tested like code ([D-008](../../../docs/DECISIONS.md#d-008)).
 * These schemas are the *structural* half of that: shapes, types, required fields.
 * The half that knows about teaching — never review a word before it is taught,
 * a chapter has 4–9 lessons — lives in `lint.ts`, because those rules need to see
 * the whole course at once and these do not.
 *
 * Everything is `.strict()`. A typo'd key in a hand-authored JSON file is a
 * silently ignored field, which is exactly the kind of mistake that surfaces
 * months later as "why is there no audio on that one exercise".
 */
import { EXERCISE_TYPES, NON_MIC_FALLBACKS } from '@capytalk/core';
import { z } from 'zod';
import { SCENE_REFS } from './scenes.generated.js';
/**
 * The exercise vocabulary and the mic fallbacks are defined in `@capytalk/core`
 * and re-exported here, so authoring tools keep importing them from `content`
 * while there is exactly one list. Two lists of sixteen strings would disagree
 * eventually, and the disagreement would show up as content that validates and
 * then cannot be built into a lesson.
 */
export { EXERCISE_TYPES, NON_MIC_FALLBACKS };
/** Dotted lowercase segments, matching the ID grammar in `@capytalk/core`. */
const contentId = z
    .string()
    .regex(/^[a-z0-9]+(?:[_-][a-z0-9]+)*(?:\.[a-z0-9]+(?:[_-][a-z0-9]+)*)*$/, 'expected dot-separated lowercase segments, e.g. "en.b1.c3.l2"');
/** A reference to an asset that the linter resolves against the filesystem. */
const assetRef = z.string().min(1);
const nonEmpty = z.string().min(1);
export const BANDS = [1, 2, 3, 4, 5];
const band = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
]);
export const LESSON_KINDS = [
    'intro',
    'practice',
    'listening',
    'speaking',
    'story',
    'review',
    'chapter_test',
    'bonus',
];
// --- exercise payloads ------------------------------------------------------
const option = z
    .object({
    id: z.string().optional(),
    label: z.string().optional(),
    imageRef: assetRef.optional(),
    correct: z.boolean(),
})
    .strict();
/**
 * The five exercise shapes a story may use as a comprehension check
 * ([Stories](../../../docs/STORIES.md#check-types-inside-a-story)).
 *
 * Declared once and used twice — here, and inside the story payload below — so
 * a check is *literally* an exercise the lesson runner already renders. Writing
 * the shapes out a second time for stories is how a check ends up parsing here
 * and drawing "this exercise is broken" there.
 */
const listenChoose = z
    .object({
    type: z.literal('listen_choose'),
    audioRef: assetRef,
    prompt: nonEmpty,
    options: z.array(option).min(2),
})
    .strict();
const wordBankOrder = z
    .object({
    type: z.literal('word_bank_order'),
    audioRef: assetRef,
    hintText: z.string().optional(),
    l2Text: nonEmpty,
    tiles: z.array(nonEmpty).min(2),
    decoys: z.array(nonEmpty).default([]),
    acceptedAlternatives: z.array(nonEmpty).default([]),
})
    .strict();
const speakRepeat = z
    .object({
    type: z.literal('speak_repeat'),
    audioRef: assetRef,
    l2Text: nonEmpty,
    targetPhonemes: z.array(nonEmpty).default([]),
    toleranceBand: z.number().min(0).max(1),
})
    .strict();
const meaningChoose = z
    .object({
    type: z.literal('meaning_choose'),
    audioRef: assetRef,
    l2Text: nonEmpty,
    options: z.array(option).min(2),
})
    .strict();
const fillBlank = z
    .object({
    type: z.literal('fill_blank'),
    sentenceText: nonEmpty,
    blankIndex: z.number().int().min(0),
    options: z.array(nonEmpty).optional(),
    accepted: z.array(nonEmpty).min(1),
})
    .strict();
/** What a story may ask, by type. The order is the order they are documented in. */
export const STORY_CHECK_TYPES = [
    'meaning_choose',
    'listen_choose',
    'word_bank_order',
    'speak_repeat',
    'fill_blank',
];
/**
 * Checks that make a child *produce* rather than recognise.
 *
 * The last check of every story must be one of these: ending on "I could say
 * that" is the whole point of a story ([Stories](../../../docs/STORIES.md)).
 */
export const STORY_PRODUCTION_CHECKS = [
    'word_bank_order',
    'speak_repeat',
    'fill_blank',
];
const storyCheck = z.discriminatedUnion('type', [
    meaningChoose,
    listenChoose,
    wordBankOrder,
    speakRepeat,
    fillBlank,
]);
const payloads = [
    listenChoose,
    z
        .object({
        type: z.literal('audio_word_match'),
        pairs: z
            .array(z.object({ l2Text: nonEmpty, audioRef: assetRef }).strict())
            .min(2),
        distractors: z.array(nonEmpty).default([]),
    })
        .strict(),
    wordBankOrder,
    speakRepeat,
    z
        .object({
        type: z.literal('speak_answer'),
        audioRef: assetRef,
        questionText: nonEmpty,
        acceptedAnswers: z.array(nonEmpty).min(1),
    })
        .strict(),
    z
        .object({
        type: z.literal('match_pairs'),
        pairs: z
            .array(z
            .object({ left: nonEmpty, right: nonEmpty, audioRef: assetRef.optional() })
            .strict())
            .min(2),
    })
        .strict(),
    z
        .object({
        type: z.literal('picture_choice'),
        audioRef: assetRef,
        l2Text: nonEmpty,
        options: z.array(option.extend({ imageRef: assetRef })).min(2),
    })
        .strict(),
    z
        .object({
        type: z.literal('build_sentence'),
        imageRef: assetRef,
        l2Text: nonEmpty,
        tiles: z.array(nonEmpty).min(2),
        decoys: z.array(nonEmpty).default([]),
        acceptedAlternatives: z.array(nonEmpty).default([]),
    })
        .strict(),
    meaningChoose,
    z
        .object({
        type: z.literal('type_answer'),
        prompt: nonEmpty,
        imageRef: assetRef.optional(),
        accepted: z.array(nonEmpty).min(1),
        maxEditDistance: z.number().int().min(0).max(3).optional(),
    })
        .strict(),
    fillBlank,
    z
        .object({
        type: z.literal('dictation'),
        audioRef: assetRef,
        l2Text: nonEmpty,
        mode: z.enum(['wordbank', 'type']),
    })
        .strict(),
    z
        .object({
        type: z.literal('story'),
        /**
         * The illustrated scene ([Stories](../../../docs/STORIES.md)).
         *
         * Still optional — a story reads perfectly well without a stage, and the
         * two Book 1 chapters that get one early should not be blocked on
         * choosing a backdrop. What changed is what an author may write: the
         * stages exist now ([D-113](../../../docs/DECISIONS.md#d-113)), so a
         * `sceneRef` naming one that does not is a typo, and a typo here renders
         * as nothing at all rather than as an error anybody would notice.
         *
         * Checked against the ids rather than the drawings: the list is generated
         * beside them from one table, so this cannot fall out of step with what
         * the app can actually draw.
         */
        sceneRef: z
            .enum(SCENE_REFS)
            .optional()
            .describe('one of the generated story stages'),
        /** Who is in it. `sneakers` plus one guest — he always plays himself. */
        cast: z.array(nonEmpty).min(1).default(['sneakers']),
        lines: z
            .array(z.object({ speaker: nonEmpty, text: nonEmpty, audioRef: assetRef }).strict())
            .min(2),
        /**
         * Comprehension, at the moment it is live rather than at the end.
         *
         * A check *is* an exercise — the same five payloads the lesson runner
         * already draws — rather than a shape of its own
         * ([Stories](../../../docs/STORIES.md#check-types-inside-a-story)).
         * `afterLine` is 1-based, matching how the lines are numbered when a
         * story is authored and discussed.
         */
        checks: z
            .array(z.object({ afterLine: z.number().int().min(1), check: storyCheck }).strict())
            .default([]),
        /** The three to five phrases the story exists to consolidate. */
        teaches: z.array(nonEmpty).default([]),
    })
        .strict(),
    z
        .object({
        type: z.literal('sound_focus'),
        pair: z.tuple([nonEmpty, nonEmpty]),
        /**
         * The two words as phoneme sequences. Required, because the rule this
         * exercise exists to enforce — the pair differs by exactly one sound —
         * cannot be checked from spelling: `think` and `sink` are one phoneme
         * apart and two letters apart.
         */
        phonemes: z.tuple([z.array(nonEmpty).min(1), z.array(nonEmpty).min(1)]),
        contrast: nonEmpty,
        audioRefs: z.tuple([assetRef, assetRef]),
    })
        .strict(),
    z
        .object({
        type: z.literal('order_dialogue'),
        lines: z
            .array(z
            .object({
            text: nonEmpty,
            audioRef: assetRef,
            position: z.number().int().min(0),
        })
            .strict())
            .min(2),
    })
        .strict(),
    z
        .object({
        type: z.literal('label_scene'),
        /** One of the drawn stages, whose props the hitboxes are computed from (D-155). */
        sceneRef: z.enum(SCENE_REFS),
        targets: z
            .array(z
            .object({
            text: nonEmpty,
            audioRef: assetRef,
            hitbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
        })
            .strict())
            .min(2),
    })
        .strict(),
    z
        .object({
        type: z.literal('gap_text'),
        /**
         * Tap a word from the bank into each gap, or type it
         * ([D-184](../../../docs/DECISIONS.md#d-184)). Choosing is the early
         * books' way and typing the later ones'.
         */
        mode: z.enum(['choose', 'type']),
        /**
         * A few sentences, one per line, a gap written as `[word]` or
         * `[word|other spelling]`. A line may start with who says it —
         * `Pip: I [like] it.` — when it comes from a story.
         */
        lines: z.array(nonEmpty).min(2).max(8),
        /** Typed gaps show this many first letters, never more than half a word. */
        hintLetters: z.number().int().min(0).max(3).default(0),
        /** Wrong words in the bank, for `choose`. None for `type`. */
        distractors: z.array(nonEmpty).default([]),
    })
        .strict(),
];
export const ExercisePayloadSchema = z.discriminatedUnion('type', payloads);
export const ExerciseSchema = z
    .object({
    id: contentId,
    type: z.enum(EXERCISE_TYPES),
    minBand: band,
    maxBand: band,
    requiresMic: z.boolean(),
    requiresAudio: z.boolean(),
    /** Required by the linter whenever `requiresMic` is true. */
    nonMicFallback: z.enum(NON_MIC_FALLBACKS).optional(),
    /** Required by the linter whenever `requiresAudio` is true, except `dictation`. */
    noAudioFallback: z.string().optional(),
    lexemeIds: z.array(contentId).default([]),
    skillIds: z.array(contentId).default([]),
    payload: ExercisePayloadSchema,
})
    .strict()
    .refine((e) => e.minBand <= e.maxBand, {
    message: 'minBand must not exceed maxBand',
    path: ['minBand'],
})
    .refine((e) => e.payload.type === e.type, {
    message: 'payload.type must match the exercise type',
    path: ['payload', 'type'],
});
export const LessonSchema = z
    .object({
    id: contentId,
    kind: z.enum(LESSON_KINDS),
    titleText: nonEmpty,
    estSeconds: z.number().int().positive(),
    narration: z
        .object({ intro: contentId.optional(), outro: contentId.optional() })
        .strict()
        .default({}),
    exercises: z.array(ExerciseSchema).min(1),
})
    .strict();
/**
 * The longest a book's or chapter's theme line may be: the `theme` column is
 * 64 characters, and a theme over it fails the seed in production rather
 * than here.
 */
export const THEME_MAX = 64;
export const CEFR_LEVELS = ['pre-A1', 'A1', 'A1+', 'A2', 'A2+', 'B1'];
export const ChapterSchema = z
    .object({
    id: contentId,
    titleText: nonEmpty,
    themeText: z.string().max(THEME_MAX).default(''),
    /** Where this chapter sits on the ladder. Stored, not derived from the book. */
    cefr: z.enum(CEFR_LEVELS),
    ordinal: z.number().int().positive(),
    /** Lexemes this chapter is the first to teach. Drives the review invariant. */
    introducesLexemeIds: z.array(contentId).default([]),
    lessonIds: z.array(contentId).min(1),
})
    .strict();
export const BookSchema = z
    .object({
    id: contentId,
    titleText: nonEmpty,
    themeText: z.string().max(THEME_MAX).default(''),
    ordinal: z.number().int().positive(),
    /**
     * A book spans a range, not a point: Book 1 runs pre-A1 to A1
     * ([Curriculum](../../../docs/CURRICULUM.md)). Two fields rather than one,
     * because the range is what the book promises.
     */
    cefrStart: z.enum(CEFR_LEVELS),
    cefrEnd: z.enum(CEFR_LEVELS),
    chapterIds: z.array(contentId).min(1),
})
    .strict();
export const CourseSchema = z
    .object({
    id: contentId,
    /** The (L1, L2) pair. English taught in English is `l1 === l2` (D-017). */
    l1: nonEmpty,
    l2: nonEmpty,
    titleText: nonEmpty,
    contentVersion: z.string().regex(/^\d+\.\d+\.\d+$/, 'expected semver'),
    bookIds: z.array(contentId).min(1),
})
    .strict();
export const PARTS_OF_SPEECH = [
    'noun',
    'verb',
    'adjective',
    'adverb',
    'pronoun',
    'preposition',
    'determiner',
    'conjunction',
    'interjection',
    'phrase',
];
export const LexemeSchema = z
    .object({
    id: contentId,
    l2Text: nonEmpty,
    glossText: nonEmpty,
    /** Drives the collection's filters, and nothing the child ever reads. */
    pos: z.enum(PARTS_OF_SPEECH),
    cefr: z.enum(CEFR_LEVELS),
    ipa: z.string().default(''),
    phonemes: z.array(nonEmpty).default([]),
    audioRef: assetRef,
    imageRef: assetRef.optional(),
    /**
     * A noun's grammatical gender, in a language that has one: a Swedish
     * *en*-word is `common`, an *ett*-word `neuter` (D-190). The lint holds
     * every article in front of it to this.
     */
    gender: z.enum(['common', 'neuter']).optional(),
    /** The chapter that first teaches this word. Nothing may use it earlier. */
    firstChapterId: contentId,
})
    .strict();
export const SKILL_KINDS = ['grammar', 'sound', 'vocabulary', 'pragmatics'];
export const SkillSchema = z
    .object({
    id: contentId,
    /** `articles`, `sound_th` — the code the engine tags exercises with. */
    code: z
        .string()
        .regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/, 'expected a lowercase snake_case code'),
    kind: z.enum(SKILL_KINDS),
    labelText: nonEmpty,
    descriptionText: z.string().default(''),
})
    .strict();
export const NarrationLineSchema = z
    .object({
    id: contentId,
    text: nonEmpty,
    audioRef: assetRef,
})
    .strict();
/**
 * A wardrobe item. `tintBase` is not optional and there is no `tintable` flag:
 * every item is tintable ([D-041](../../../docs/DECISIONS.md#d-041)), so an item
 * without a base colour fails here rather than shipping un-dyeable.
 */
/**
 * A Long Path template ([Generation](../../../docs/GENERATION.md),
 * [D-150](../../../docs/DECISIONS.md#d-150)): a reviewed sentence frame whose
 * slots each list the exact words that may fill them. Reviewed once, correct
 * for every filling — which is why a slot is a list, not a category.
 */
export const TemplateSchema = z
    .object({
    id: z.string().regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/, 'expected a snake_case slug'),
    expedition: nonEmpty,
    frame: nonEmpty,
    slots: z.record(z.string().regex(/^[a-z0-9_]+$/, 'expected a snake_case slot name'), z.array(contentId).min(2)),
    picture: z.string().nullable(),
    blank: z
        .object({ word: nonEmpty, options: z.array(nonEmpty).min(1) })
        .strict()
        .nullable(),
    decoys: z.array(nonEmpty).default([]),
    skillIds: z.array(contentId).default([]),
})
    .strict();
export const ShopItemSchema = z
    .object({
    id: z
        .string()
        .regex(/^[a-z0-9]+(?:[_-][a-z0-9]+)*$/, 'expected one lowercase slug'),
    slot: z.enum(['hat', 'glasses', 'top', 'neck', 'shoes', 'held']),
    nameText: nonEmpty,
    rarity: z.enum(['common', 'rare', 'epic', 'seasonal']),
    priceAcorns: z.number().int().positive(),
    /**
     * Optional, and usually absent.
     *
     * A wardrobe item is markup with a hole in it, generated from
     * [`tools/art/catalogue.py`](../../../tools/art/catalogue.py) and rendered
     * inline — there is no file to point at
     * ([D-080](../../../docs/DECISIONS.md#d-080)). The field stays for anything
     * that ever does ship as a picture.
     */
    assetRef: assetRef.optional(),
    tintBase: z.string().regex(/^#[0-9a-f]{6}$/, 'expected a lowercase hex colour'),
    season: z.string().optional(),
})
    .strict();
//# sourceMappingURL=schema.js.map